import React, { Fragment, useRef, useState } from "react";
import { Grid, IconButton, Typography, Box, CircularProgress } from "@mui/material";
import { Edit } from "@mui/icons-material";
import LoopTwoToneIcon from '@mui/icons-material/LoopTwoTone';
import * as exifr from 'exifr';
import ngeohash from "ngeohash";
import { icons, truncateString } from "../constants";
import { GeoPoint } from "firebase/firestore";
import ImageCropModal from "./ImageCropModal";
import ConfirmationModal from "./ConfirmationModal";
import { extractAllExifData, storeExifDataBackup } from "../utils/exifUtils";
import { getLocationFromCoordinates } from "../utils/locationUtils";
import { GOOGLE_MAPS_API_KEY } from "../utils/googleMapsLoader";

const QTDetailsImageWithLabelSection = ({
  label,
  image,
  fileName,
  handleImagePreviewModalOpen,
  editEnabled,
  details,
  setDetails,
  uploadImage,
  setUploadImage,
  isCreateMode = false,
  autoOpenCropOnUpload = false,
  disablePreview = false,
  showConvertToJpgButton = false,
  onConvertToJpg,
  convertingImage = false,
}) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [originalExifData, setOriginalExifData] = useState(null);
  const [exifLocationModalOpen, setExifLocationModalOpen] = useState(false);
  const [pendingExifLocation, setPendingExifLocation] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Shared processing used by both the file input and drag-and-drop.
  const processFile = async (file) => {
    if (!file) return;

    console.log("🔵 INITIAL UPLOAD - File properties:", {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    // Extract and store complete EXIF data before any processing
    const fullExifData = await extractAllExifData(file);
    setOriginalExifData(fullExifData);

    const blobUrl = URL.createObjectURL(file);
    console.log("🔵 INITIAL UPLOAD - Blob URL created:", blobUrl);

    const uploadImageData = {
      preview: blobUrl,
      raw: file,
    };

    console.log("🔵 INITIAL UPLOAD - uploadImage object:", uploadImageData);
    setUploadImage(uploadImageData);

    if (label === "Org Photo") {
      setDetails({ ...details, originalPhotoName: file?.name });
    } else {
      setDetails({ ...details, originalPhotoNameOP: file?.name });
    }

    // Automatically open crop modal if autoOpenCropOnUpload is enabled
    if (autoOpenCropOnUpload) {
      setCropModalOpen(true);
    }

    // Check for EXIF GPS data and show confirmation modal
    extractExifData(file, async (exifData) => {
      console.log("exif data:::", exifData)
      if (exifData) {
        const { latitude, longitude } = exifData;

        console.log("Image has GPS data:", latitude, longitude);

        // Get full location data from coordinates
        try {
          const locationData = await getLocationFromCoordinates(latitude, longitude);

          // Store the pending location data
          setPendingExifLocation(locationData);

          // Show confirmation modal
          setExifLocationModalOpen(true);
        } catch (error) {
          console.error("Error getting location from EXIF coordinates:", error);
        }
      } else {
        console.log("No EXIF GPS data found in the image.");
      }
    });
  };

  const handleFileInputChange = async (event) => {
    const file = event.target.files[0];

    // If no file selected (user cancelled), preserve existing state
    if (!file) {
      console.log("🔵 INITIAL UPLOAD - File selection cancelled, preserving existing image");
      // Clear the file input to reset its state
      event.target.value = '';
      return;
    }

    await processFile(file);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  // ─── Drag-and-drop handlers ───
  const handleDragEnter = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      console.warn("Dropped file is not an image:", file.type);
      return;
    }
    await processFile(file);
  };

  // Shared drag-and-drop props for image drop zones.
  const dropZoneProps = editEnabled
    ? {
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
      }
    : {};


  const extractExifData = async (file, callback) => {
    try {
      const exifData = await exifr.parse(file);
      if (exifData && exifData.latitude && exifData.longitude) {
        const latitude = exifData.latitude;
        const longitude = exifData.longitude;
        callback({ latitude, longitude });
      } else {
        callback(null);
      }
    } catch (error) {
      console.error("Error extracting EXIF data: ", error);
      callback(null);
    }
  };

  const getAddressFromCoordinates = async (lat, lon) => {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${GOOGLE_MAPS_API_KEY}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        return data.results[0].formatted_address;
      }
      if (data.status && data.status !== "OK") {
        console.warn(
          `Geocode lookup failed - status: ${data.status}. ${
            data.error_message || ""
          }`
        );
      }
      return null;
    } catch (error) {
      console.error("Error fetching address: ", error);
      return null;
    }
  };

  const handleEditClick = async () => {
    // If we don't have EXIF data yet, extract it from the current image
    if (!originalExifData && (uploadImage?.raw || image)) {
      try {
        let file;
        if (uploadImage?.raw) {
          file = uploadImage.raw;
        } else if (image) {
          // Convert image URL to file for EXIF extraction
          const response = await fetch(image);
          const blob = await response.blob();
          file = new File([blob], fileName || 'image.jpg', { type: blob.type });
        }
        
        if (file) {
          const fullExifData = await extractAllExifData(file);
          setOriginalExifData(fullExifData);
        }
      } catch (error) {
        console.error('Error extracting EXIF data for editing:', error);
      }
    }
    
    setCropModalOpen(true);
  };

  const handleCropModalClose = () => {
    setCropModalOpen(false);
  };

  const handleCropSave = async (editedImageData) => {
    try {
      setLoading(true);
      
      console.log("🔴 EDIT SAVE - Received editedImageData:", {
        preview: editedImageData.preview ? editedImageData.preview.substring(0, 100) + "..." : "null",
        previewLength: editedImageData.preview ? editedImageData.preview.length : 0,
        rawFile: editedImageData.raw ? {
          name: editedImageData.raw.name,
          size: editedImageData.raw.size,
          type: editedImageData.raw.type
        } : "null",
        exifData: editedImageData.exifData ? "present" : "null"
      });
      
      const newUploadImageData = {
        preview: editedImageData.preview,
        raw: editedImageData.raw,
      };
      
      console.log("🔴 EDIT SAVE - Setting new uploadImage:", newUploadImageData);
      
      // Update the upload image with edited version
      setUploadImage(newUploadImageData);

      // Update filename if needed
      const editedFileName = `edited_${fileName || 'image.jpg'}`;
      if (label === "Org Photo") {
        setDetails({ ...details, originalPhotoName: editedFileName });
      } else {
        setDetails({ ...details, originalPhotoNameOP: editedFileName });
      }

      // Re-extract GPS data from the edited image (EXIF should be preserved)
      //if (editedImageData.exifData && editedImageData.exifData.latitude && editedImageData.exifData.longitude) {
      //  const { latitude, longitude } = editedImageData.exifData;
      //  const geohash = ngeohash.encode(latitude, longitude);
      //  const address = await getAddressFromCoordinates(latitude, longitude);

     //   console.log("Edited image GPS data preserved:", { latitude, longitude, address });

     //   setDetails(prevDetails => ({
     //     ...prevDetails,
     //     location: address,
     //     coordinates: new GeoPoint(latitude, longitude),
     //     g: { geohash, geopoint: new GeoPoint(latitude, longitude) },
     //   }));
     // }

      setCropModalOpen(false);
    } catch (error) {
      console.error('Error saving edited image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseExifLocation = () => {
    if (pendingExifLocation) {
      console.log("Replacing location with EXIF data:", pendingExifLocation);
      
      setDetails(prevDetails => ({
        ...prevDetails,
        ...pendingExifLocation
      }));
      
      setPendingExifLocation(null);
    }
    setExifLocationModalOpen(false);
  };

  const handleDeclineExifLocation = () => {
    console.log("User declined to use EXIF location");
    setPendingExifLocation(null);
    setExifLocationModalOpen(false);
  };

  // Determine styles based on create mode
  const currentImageStyles = isCreateMode ? createModeImageStyles : imageStyles;
  const containerStyles = isCreateMode ? createModeContainerStyles : { marginBottom: 24 };
  const previewEnabled = !disablePreview && typeof handleImagePreviewModalOpen === "function";
  console.log("isCreateMode:", isCreateMode, " containerStyles:", containerStyles);

  const hasImage = !!(image || uploadImage);

  return (
    <Grid container alignItems="flex-start" style={containerStyles}>
      {isCreateMode ? (
        // Create mode layout: Label and buttons vertically centered on the image
        <>
          {/* Label column - centered on image height */}
          <Grid container alignItems="center" style={{ width: "30%", height: 200 }}>
            <span style={titleTextStyle}>{label}</span>
          </Grid>
          
          {/* Image and filename column */}
          <Grid container alignItems="center" style={{ width: "auto", marginRight: 20 }}>
           {hasImage ? (
              <>
                <Box
                  {...dropZoneProps}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  <img
                    src={
                      uploadImage != null && uploadImage.preview
                        ? uploadImage.preview
                        : image
                    }
                    className="image-preview"
                    alt="qt image"
                    onClick={previewEnabled ? handleImagePreviewModalOpen : undefined}
                    style={{
                      ...currentImageStyles,
                      cursor: previewEnabled ? "pointer" : "default",
                      pointerEvents: previewEnabled ? "auto" : "none",
                      border: isDragging ? "3px solid #008c9e" : undefined,
                      boxShadow: isDragging
                        ? "0 0 0 4px rgba(0,167,205,0.25)"
                        : undefined,
                    }}
                  />
                  {isDragging && editEnabled && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "4px",
                        backgroundColor: "rgba(0,167,205,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#008c9e",
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        Drop to replace
                      </Typography>
                    </Box>
                  )}
                </Box>
                <span style={{ ...subTitleTextStyle, marginLeft: 15 }}>
                  {fileName}
                </span>
              </>
            ) : (
              <>
                <Box
                  onClick={editEnabled ? handleFileUploadClick : undefined}
                  {...dropZoneProps}
                  role={editEnabled ? "button" : undefined}
                  tabIndex={editEnabled ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (!editEnabled) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleFileUploadClick();
                    }
                  }}
                  sx={{
                    width: 200,
                    height: 200,
                    borderRadius: 2,
                    border: isDragging
                      ? "2px dashed #008c9e"
                      : "2px solid #00A7CD",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: editEnabled ? "pointer" : "default",
                    userSelect: "none",
                    gap: 1.25,
                    backgroundColor: isDragging ? "#E5F6FA" : "transparent",
                    transition:
                      "background-color 0.15s ease, border-color 0.15s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#00A7CD",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={icons.UploadIcon}
                      alt="upload"
                      style={{ width: 26, height: 26, filter: "brightness(0) invert(1)" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: isDragging ? "#008c9e" : "#595959",
                      fontSize: 14,
                      fontWeight: isDragging ? 600 : 400,
                      fontFamily: "Avenir Book, sans-serif",
                    }}
                  >
                    {isDragging ? "Drop photo here" : "Add Photo"}
                  </Typography>
                </Box>
              </>
            )}
          </Grid>

          
          {/* File input + buttons column - centered on image height */}
          {editEnabled ? (
            <>
              <input
                type="file"
                style={{ display: "none" }}
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              {hasImage ? (
                <Grid container alignItems="center" style={{ width: "auto", height: 200 }}>
                  <img
                    src={icons.UploadIcon}
                    alt="upload icon"
                    onClick={handleFileUploadClick}
                    style={{ ...uploadIconStyles, marginLeft: 0, marginRight: 15 }}
                  />
                  <IconButton
                    onClick={handleEditClick}
                    size="small"
                    sx={{
                      marginLeft: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Grid>
              ) : null}
            </>
          ) : null}
        </> 
      ) : (
        // Normal mode layout: Original horizontal layout
        <>
          {/* Label column - centered on image height */}
          {shouldShow(label) ? (
            <Grid container alignItems="center" style={{ width: "25%", height: 60 }}>
              <span style={titleTextStyle}>{label}</span>
            </Grid>
          ) : null}
          
          {/* Image and filename column */}
          <Grid container alignItems="center" style={{ width: "auto", marginRight: 20 }}>
            {image || uploadImage ? (
              <>
                <Box
                  {...dropZoneProps}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  <img
                    src={
                      uploadImage != null && uploadImage.preview
                        ? uploadImage.preview
                        : image
                    }
                    className="image-preview"
                    alt="qt image"
                    onClick={previewEnabled ? handleImagePreviewModalOpen : undefined}
                    style={{
                      ...currentImageStyles,
                      cursor: previewEnabled ? "pointer" : "default",
                      pointerEvents: previewEnabled ? "auto" : "none",
                      border: isDragging ? "2px solid #008c9e" : undefined,
                      boxShadow: isDragging
                        ? "0 0 0 3px rgba(0,167,205,0.25)"
                        : undefined,
                    }}
                  />
                  {isDragging && editEnabled && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0,167,205,0.15)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </Box>
                {shouldShow(fileName) ? (
                  <span style={{ ...subTitleTextStyle, marginLeft: 15 }}>
                    {fileName}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <div
                  {...dropZoneProps}
                  style={{
                    width: 60,
                    height: 60,
                    border: isDragging
                      ? '2px dashed #008c9e'
                      : '2px dashed #ccc',
                    backgroundColor: isDragging ? '#E5F6FA' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      ...subTitleTextStyle,
                      marginLeft: 0,
                      color: isDragging ? '#008c9e' : subTitleTextStyle.color,
                    }}
                  >
                    {isDragging ? 'Drop' : 'No photo'}
                  </span>
                </div>
                {shouldShow(fileName) ? (
                  <span style={{ ...subTitleTextStyle, marginLeft: 15 }}>
                    No file selected
                  </span>
                ) : null}
              </>
            )}
          </Grid>

          
          {/* Buttons column - centered on image height */}
          {editEnabled && (
            <Grid container alignItems="center" style={{ width: "auto", height: 60 }}>
              <input
                type="file"
                style={{ display: "none" }}
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              <img
                src={icons.UploadIcon}
                alt="upload icon"
                onClick={handleFileUploadClick}
                style={{ ...uploadIconStyles, marginLeft: 0, marginRight: 15 }}
              />
              {(image || uploadImage) && (
                <IconButton
                  onClick={handleEditClick}
                  size="small"
                  sx={{ 
                    marginLeft: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    }
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
              {showConvertToJpgButton && image && !uploadImage && (
                convertingImage ? (
                  <CircularProgress size={22} style={{ marginLeft: 15 }} />
                ) : (
                  <LoopTwoToneIcon
                    fontSize="medium"
                    onClick={onConvertToJpg}
                    style={convertToJpgIconStyles}
                    titleAccess="Convert Image to JPG"
                  />
                )
              )}
            </Grid>
          )}
        </> 
      )}
      {loading && <p>Processing...</p>}
      
      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropModalClose}
        imageSrc={
          uploadImage?.preview || image
        }
        onSave={handleCropSave}
        originalFileName={fileName}
      />
      
      {/* EXIF Location Confirmation Modal */}
      <ConfirmationModal
        open={exifLocationModalOpen}
        onClose={handleDeclineExifLocation}
        onConfirm={handleUseExifLocation}
        title="Use Image Location?"
        message="This image contains location data. Would you like to use the image location for this QuickTale?"
      />
    </Grid>
  );
};

export default QTDetailsImageWithLabelSection;

const titleTextStyle = {
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

const shouldShow = (value) => {
  if (value === 0) return true;
  if (value === false) return true;
  return !!(value && String(value).trim().length > 0);
};

const imageStyles = { width: 60, height: 60, cursor: "pointer", objectFit: 'contain' };

const createModeImageStyles = { 
  width: 200, 
  height: 200, 
  cursor: "pointer", 
  objectFit: 'contain',
  borderRadius: 4
};

const createModeContainerStyles = {
  marginBottom: 24,
  flexDirection: 'row',
  alignItems: 'flex-start'
};

const uploadIconStyles = {
  width: 24,
  height: 24,
  cursor: "pointer",
  marginLeft: 15,
};

const convertToJpgIconStyles = {
  color: "#00A7CD",
  cursor: "pointer",
  marginLeft: 15,
  fontSize: 24,
};

const subTitleTextStyle = {
  color: "#000",
  fontSize: 14,
  marginLeft: 30,
  fontFamily: "Avenir Book, sans-serif",
};
